import { useState, useEffect } from "react";
import { useSearch } from "wouter";
import { Zap, Check, ArrowRight, Loader2, AlertCircle, Shield } from "lucide-react";
import { Wordmark } from "@/components/wordmark";

// One plan (Bader, 2026-11-02): $29.99/month with a monthly AI allowance; the server sells the single active package
const PLANS: Array<{ id: string; name: string; price: string; period: string; desc: string; features: string[]; popular?: boolean }> = [
  {
    id: "drpr",
    name: "DrPR",
    price: "$29.99",
    period: "/month",
    desc: "Your AI marketing team: strategy, content, leads and research",
    features: [
      "All 5 AI agents (Draper, Peggy, Pete, Rogers, Harry)",
      "Blogs and social posts written, checked and scheduled for you",
      "Website chat that answers and books leads",
      "A monthly AI allowance, shown in Settings, that resets on the 1st",
      "You approve before anything goes out, or let it run on its own",
      "Cancel any time",
    ],
  },
];

export default function SignupPage() {
  const searchString = useSearch();
  const params = new URLSearchParams(searchString);
  const inviteCode = params.get("invite");
  const poolCode = params.get("pool");

  const [inviteData, setInviteData] = useState<{ plan: string; expiresAt: string } | null>(null);
  const [inviteError, setInviteError] = useState("");
  const [loading, setLoading] = useState(!!inviteCode);
  const [selectedPlan, setSelectedPlan] = useState<string>("drpr");

  useEffect(() => {
    if (!inviteCode) return;
    setLoading(true);
    fetch(`/api/invite/${inviteCode}`)
      .then(async (r) => {
        if (!r.ok) {
          const data = await r.json();
          throw new Error(data.message || "Invalid invite code");
        }
        return r.json();
      })
      .then((data) => {
        setInviteData(data);
        setSelectedPlan(data.plan);
      })
      .catch((e) => setInviteError(e.message))
      .finally(() => setLoading(false));
  }, [inviteCode]);

  if (poolCode) {
    return (
      <div className="min-h-screen bg-[#060910] flex flex-col items-center justify-center p-6" data-testid="signup-pool-invite">
        <div className="max-w-sm w-full text-center">
          <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mx-auto mb-6">
            <span className="text-3xl">⚽</span>
          </div>
          <h1 className="text-3xl font-black text-white mb-2 tracking-tight">One step away.</h1>
          <p className="text-white/50 text-sm mb-1">Create your free Drpr account to make your picks.</p>
          <p className="text-white/30 text-xs mb-8">You'll have full access to the app — set up your workspace anytime.</p>
          <button
            data-testid="button-pool-signup-continue"
            onClick={() => {
              sessionStorage.setItem("drpr_pool_invite", poolCode);
              window.location.href = "/api/login";
            }}
            className="w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-emerald-500 hover:bg-emerald-400 text-black font-black rounded-2xl text-base transition-all shadow-[0_0_30px_rgba(52,211,153,0.25)] cursor-pointer"
          >
            <span>Create Free Account</span>
            <ArrowRight size={18} />
          </button>
          <p className="mt-4 text-white/30 text-xs">
            Already have an account?{" "}
            <a href={`/?pool=${poolCode}`} className="text-emerald-400 hover:text-emerald-300 transition-colors">Sign in</a>
          </p>
          <div className="mt-6 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
            <p className="text-xs text-amber-300">Drpr.ai does not collect, hold, or distribute any funds. All financial arrangements are between participants.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030308] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!inviteCode) {
    return (
      <div className="min-h-screen bg-[#030308] flex flex-col items-center justify-center p-6" data-testid="signup-no-invite">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(59,130,246,0.3)]">
          <Shield size={28} className="text-foreground" />
        </div>
        <h1 className="text-2xl font-display font-bold text-foreground mb-2">Invite Only</h1>
        <p className="text-foreground/30 text-sm text-center max-w-sm">
          Drpr.ai is currently invite-only. You need a valid invite link to sign up.
          Contact us or check if you have received an invitation.
        </p>
        <a href="/" className="mt-6 text-blue-400 text-sm hover:text-blue-300 transition-colors" data-testid="link-back-home">
          Back to Home
        </a>
      </div>
    );
  }

  if (inviteError) {
    return (
      <div className="min-h-screen bg-[#030308] flex flex-col items-center justify-center p-6" data-testid="signup-invite-error">
        <div className="flex items-center gap-2 px-4 py-3 mb-6 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          <AlertCircle size={16} />
          <span>{inviteError}</span>
        </div>
        <a href="/" className="text-blue-400 text-sm hover:text-blue-300 transition-colors">Back to Home</a>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030308] py-16 px-6" data-testid="signup-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <Wordmark fs={40} />
          </div>
          <h1 className="text-2xl font-display font-bold text-foreground mt-4 mb-2" data-testid="text-signup-title">Choose Your Plan</h1>
          <p className="text-foreground/30 text-sm">Start deploying your AI marketing agents today.</p>
          {inviteData && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 mt-4 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium">
              <Check size={12} />
              Valid invite for {inviteData.plan} plan
            </div>
          )}
        </div>

        <div className="grid gap-6 max-w-md mx-auto w-full">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-foreground/[0.02] border rounded-2xl p-6 transition-all cursor-pointer ${
                selectedPlan === plan.id
                  ? "border-blue-500/40 shadow-[0_0_30px_rgba(59,130,246,0.1)]"
                  : "border-foreground/[0.06] hover:border-foreground/[0.12]"
              }`}
              onClick={() => setSelectedPlan(plan.id)}
              data-testid={`plan-card-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-600 text-foreground text-[10px] font-bold rounded-full uppercase tracking-wider">
                  Most Popular
                </div>
              )}
              <div className="flex items-center justify-between gap-2 mb-4">
                <h3 className="text-lg font-display font-bold text-foreground">{plan.name}</h3>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedPlan === plan.id ? "border-blue-500 bg-blue-500" : "border-foreground/20"
                }`}>
                  {selectedPlan === plan.id && <Check size={12} className="text-foreground" />}
                </div>
              </div>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-3xl font-bold text-foreground">{plan.price}</span>
                <span className="text-foreground/30 text-sm">{plan.period}</span>
              </div>
              <p className="text-foreground/30 text-xs mb-5">{plan.desc}</p>
              <div className="space-y-2.5">
                {plan.features.map((f) => (
                  <div key={f} className="flex items-center gap-2.5">
                    <Check size={14} className="text-emerald-400 shrink-0" />
                    <span className="text-sm text-foreground/60">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <button
            onClick={() => {
              sessionStorage.setItem("drpr_signup_plan", selectedPlan);
              // email + password sign-up with the invite code (/api/login only exists on Replit)
              window.location.href = `/join?invite=${encodeURIComponent(inviteCode || "")}`;
            }}
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-foreground font-bold rounded-2xl text-base transition-all shadow-2xl shadow-blue-900/40 cursor-pointer"
            data-testid="button-signup-continue"
          >
            <span>Continue with Sign Up</span>
            <ArrowRight size={18} />
          </button>
          <p className="text-foreground/15 text-xs mt-4">Credits renew monthly. Overage at $0.10/credit — you control your cap.</p>
        </div>
      </div>
    </div>
  );
}
